import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Copy,
  ExternalLink,
  X,
  CheckCircle,
  Circle,
  Pencil,
  MapPin,
  Clock,
  AlertTriangle,
  User,
  Tag,
  MessageSquare,
  RefreshCw,
  Trash2,
  Mail,
  Forward,
  History,
  Phone,
  Pin,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import {
  apiClient,
  type AgendaEvent,
  type TaskDetail,
  type TaskStatus,
} from '@/lib/api-client';
import { useAuthStore } from '@/lib/auth-store';
import { cn } from '@/lib/utils';
import { linkifyHtml } from '@/lib/linkify';
import { looksLikeHtmlDocument, stripHtmlToTextPreserveBreaks } from '@/lib/html-content';
import { ActivityTab } from './task-detail-tab-activity';
import { EmailTab } from './task-detail-tab-email';
import { ForwardTab } from './task-detail-tab-forward';
import { HistoryTab } from './task-detail-tab-history';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface TaskDetailModalProps {
  taskId: string | null;
  open: boolean;
  onClose: () => void;
  onEdit?: (taskId: string) => void;
  /**
   * Evento da agenda que originou a abertura (passado pela agenda).
   * Usado para identificar ocorrencias de series recorrentes.
   */
  agendaEvent?: AgendaEvent | null;
}

type OccurrenceAction = 'editThis' | 'editSeries' | 'deleteThis' | 'deleteSeries' | null;

/** Mini-prompt inline que aparece antes de abrir o form de edição/exclusão de serie */
function OccurrencePrompt({
  action,
  onSelect,
  onCancel,
}: {
  action: 'edit' | 'delete';
  onSelect: (choice: OccurrenceAction) => void;
  onCancel: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-sm rounded-lg border border-[var(--modal-border,var(--card-border))] bg-[var(--modal-bg,var(--card-bg))] p-5 space-y-4 shadow-xl">
        <div className="flex items-center gap-2">
          <RefreshCw className="h-4 w-4 text-blue-500 shrink-0" />
          <p className="text-sm font-semibold text-[var(--fg)]">
            {action === 'edit' ? 'Editar evento recorrente' : 'Excluir evento recorrente'}
          </p>
        </div>
        <p className="text-sm text-[var(--fg-muted)]">
          Este evento faz parte de uma série recorrente. O que deseja{' '}
          {action === 'edit' ? 'editar' : 'excluir'}?
        </p>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => onSelect(action === 'edit' ? 'editThis' : 'deleteThis')}
            className="w-full rounded-md border border-[var(--border)] px-4 py-2 text-left text-sm text-[var(--fg)] hover:bg-[color-mix(in_oklch,var(--fg)_6%,transparent)] transition"
          >
            Apenas esta ocorrência
          </button>
          <button
            type="button"
            onClick={() => onSelect(action === 'edit' ? 'editSeries' : 'deleteSeries')}
            className="w-full rounded-md border border-[var(--border)] px-4 py-2 text-left text-sm text-[var(--fg)] hover:bg-[color-mix(in_oklch,var(--fg)_6%,transparent)] transition"
          >
            Toda a série
          </button>
        </div>
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="text-xs text-[var(--fg-muted)] hover:text-[var(--fg)] transition"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}

type ActiveTab = 'detalhes' | 'atividade' | 'email' | 'encaminhar' | 'historico';

const TABS: { id: ActiveTab; label: string; icon: React.ReactNode }[] = [
  { id: 'detalhes', label: 'Detalhes', icon: null },
  { id: 'atividade', label: 'Atividade', icon: <MessageSquare className="h-3.5 w-3.5" /> },
  { id: 'email', label: 'Enviar por e-mail', icon: <Mail className="h-3.5 w-3.5" /> },
  { id: 'encaminhar', label: 'Encaminhar', icon: <Forward className="h-3.5 w-3.5" /> },
  { id: 'historico', label: 'Histórico', icon: <History className="h-3.5 w-3.5" /> },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string | null, withTime = false): string {
  if (!iso) return '—';
  try {
    const d = parseISO(iso);
    return format(d, withTime ? 'dd/MM/yyyy HH:mm' : 'dd/MM/yyyy', { locale: ptBR });
  } catch {
    return iso;
  }
}

function relativeDate(iso: string): string {
  try {
    return formatDistanceToNow(parseISO(iso), { locale: ptBR, addSuffix: true });
  } catch {
    return iso;
  }
}

function statusLabel(
  status: TaskStatus,
  commentCount: number,
): { label: string; cls: string } {
  switch (status) {
    case 'completed':
      return { label: 'Concluída', cls: 'bg-gray-100 text-gray-600' };
    case 'canceled':
      return { label: 'Cancelada', cls: 'bg-red-100 text-red-700' };
    case 'in_progress':
      return { label: `Em andamento (${commentCount})`, cls: 'bg-blue-100 text-blue-700' };
    default:
      return { label: `Pendente (${commentCount})`, cls: 'bg-green-100 text-green-700' };
  }
}

function copyToClipboard(text: string) {
  try {
    navigator.clipboard.writeText(text);
  } catch {
    const el = document.createElement('textarea');
    el.value = text;
    el.style.position = 'fixed';
    el.style.opacity = '0';
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
  }
}

/** Extrai numero de telefone so digitos para link WhatsApp */
function phoneDigits(value: string): string {
  return value.replace(/\D/g, '');
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

export function TaskDetailModal({
  taskId,
  open,
  onClose,
  onEdit,
  agendaEvent,
}: TaskDetailModalProps) {
  const queryClient = useQueryClient();
  const overlayRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<ActiveTab>('detalhes');
  const [occurrencePrompt, setOccurrencePrompt] = useState<'edit' | 'delete' | null>(null);

  const tenantId = useAuthStore((s) => s.tenantId ?? '');

  // Identifica se este evento e uma ocorrencia de serie
  const isOccurrence = agendaEvent?.isOccurrence === true;
  const masterTaskId = agendaEvent?.masterTaskId ?? null;
  const occurrenceDate = agendaEvent?.occurrenceDate ?? null;

  const {
    data: task,
    isLoading,
    error,
  } = useQuery<TaskDetail>({
    queryKey: ['task', taskId],
    queryFn: () => apiClient.tasksGet(taskId!),
    enabled: open && !!taskId,
    staleTime: 30_000,
  });

  // Busca processo se vinculado
  const { data: caseData } = useQuery({
    queryKey: ['case', task?.caseId],
    queryFn: () => apiClient.casesGet(task!.caseId!),
    enabled: !!task?.caseId,
    staleTime: 5 * 60_000,
  });

  // Busca cliente se vinculado
  const { data: clientData } = useQuery({
    queryKey: ['client', task?.clientId],
    queryFn: () => apiClient.clientsGet(task!.clientId!),
    enabled: !!task?.clientId,
    staleTime: 5 * 60_000,
  });

  // Comentarios para contagem no badge de situacao
  const { data: comments = [] } = useQuery({
    queryKey: ['task', taskId, 'comments'],
    queryFn: () => apiClient.tasksListComments(taskId!),
    enabled: open && !!taskId,
    staleTime: 30_000,
  });

  // Toast inline
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  useEffect(() => {
    if (!toastMsg) return;
    const t = setTimeout(() => setToastMsg(null), 2500);
    return () => clearTimeout(t);
  }, [toastMsg]);

  // Mutation de toggle pinned
  const pinMut = useMutation({
    mutationFn: (pinned: boolean) => apiClient.tasksUpdate(taskId!, { pinned }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task', taskId] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });

  // Mutation de toggle status
  const completeMut = useMutation({
    mutationFn: () => apiClient.tasksComplete(taskId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task', taskId] });
      queryClient.invalidateQueries({ queryKey: ['agenda', 'events'] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });

  const uncompleteMut = useMutation({
    mutationFn: () => apiClient.tasksUncomplete(taskId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task', taskId] });
      queryClient.invalidateQueries({ queryKey: ['agenda', 'events'] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });

  // Mutation de exclusao de ocorrencia
  const deleteOccurrenceMut = useMutation({
    mutationFn: () =>
      apiClient.tasksDeleteOccurrence(masterTaskId!, occurrenceDate!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agenda', 'events'] });
      onClose();
    },
  });

  // Mutation de exclusao da serie inteira
  const deleteSeriesMut = useMutation({
    mutationFn: () => apiClient.tasksDelete(masterTaskId ?? taskId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agenda', 'events'] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      onClose();
    },
  });

  function handleOccurrenceAction(choice: OccurrenceAction) {
    setOccurrencePrompt(null);
    if (choice === 'editThis') {
      if (onEdit && masterTaskId) {
        onClose();
        onEdit(masterTaskId);
      }
    } else if (choice === 'editSeries') {
      if (onEdit && masterTaskId) {
        onClose();
        onEdit(masterTaskId);
      }
    } else if (choice === 'deleteThis') {
      deleteOccurrenceMut.mutate();
    } else if (choice === 'deleteSeries') {
      deleteSeriesMut.mutate();
    }
  }

  // Fecha ao clicar no overlay
  function handleOverlayClick(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === overlayRef.current) onClose();
  }

  // Fecha com ESC
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Reset aba ao abrir
  useEffect(() => {
    if (open) setActiveTab('detalhes');
  }, [open, taskId]);

  if (!open) return null;

  // Renderizar prompt de ocorrencia (sobreposto ao modal)
  if (occurrencePrompt) {
    return (
      <OccurrencePrompt
        action={occurrencePrompt}
        onSelect={handleOccurrenceAction}
        onCancel={() => setOccurrencePrompt(null)}
      />
    );
  }

  const shareUrl = `${window.location.origin}/escritorio/agenda?taskId=${taskId ?? ''}`;
  const isCompleted = task?.status === 'completed';
  const isToggling = completeMut.isPending || uncompleteMut.isPending;
  const commentCount = comments.length;
  const statusInfo = task ? statusLabel(task.status, commentCount) : null;

  // Contato de e-mail do cliente para pré-popular aba e-mail
  const clientEmail =
    clientData?.contacts.find((c) => c.type === 'email')?.value ?? null;

  // Contatos de telefone/WhatsApp do cliente para sidebar
  const clientPhones = clientData?.contacts.filter(
    (c) => c.type === 'phone' || c.type === 'whatsapp',
  ) ?? [];
  const clientEmailContact = clientData?.contacts.find((c) => c.type === 'email');

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={handleOverlayClick}
    >
      <div
        className="relative flex w-full max-w-3xl flex-col rounded-lg border border-[var(--modal-border,var(--card-border))] bg-[var(--modal-bg,var(--card-bg))] shadow-xl max-h-[90vh] overflow-hidden"
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-[var(--border)] px-5 py-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-[var(--fg)]">
              Detalhes da tarefa
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            {/* Copiar link */}
            <button
              type="button"
              title="Copiar link"
              onClick={() => copyToClipboard(shareUrl)}
              className="flex items-center gap-1 rounded px-2 py-1 text-xs text-[var(--fg-muted)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] hover:text-[var(--fg)] transition"
            >
              <Copy className="h-3.5 w-3.5" />
              Copiar link
            </button>

            {/* Editar */}
            {onEdit && taskId && (
              <button
                type="button"
                title="Editar"
                onClick={() => {
                  if (isOccurrence) {
                    setOccurrencePrompt('edit');
                  } else {
                    onClose();
                    onEdit(taskId);
                  }
                }}
                className="flex items-center gap-1 rounded px-2 py-1 text-xs text-[var(--fg-muted)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] hover:text-[var(--fg)] transition"
              >
                <Pencil className="h-3.5 w-3.5" />
                Editar
              </button>
            )}

            {/* Fixar */}
            {task && taskId && (
              <button
                type="button"
                title={task.pinned ? 'Desafixar' : 'Fixar'}
                disabled={pinMut.isPending}
                onClick={() => pinMut.mutate(!task.pinned)}
                className={cn(
                  'flex items-center gap-1 rounded px-2 py-1 text-xs transition disabled:opacity-50',
                  task.pinned
                    ? 'bg-[color-mix(in_oklch,var(--accent)_15%,transparent)] text-[var(--accent)] hover:opacity-80'
                    : 'text-[var(--fg-muted)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] hover:text-[var(--fg)]',
                )}
              >
                <Pin className={cn('h-3.5 w-3.5', task.pinned && 'fill-[var(--accent)]')} />
                {task.pinned ? 'Desafixar' : 'Fixar'}
              </button>
            )}

            {/* Excluir */}
            {taskId && (
              <button
                type="button"
                title="Excluir"
                onClick={() => {
                  if (isOccurrence) {
                    setOccurrencePrompt('delete');
                  } else {
                    if (window.confirm('Excluir esta tarefa?')) {
                      apiClient.tasksDelete(taskId).then(() => {
                        queryClient.invalidateQueries({ queryKey: ['agenda', 'events'] });
                        queryClient.invalidateQueries({ queryKey: ['tasks'] });
                        onClose();
                      });
                    }
                  }
                }}
                className="flex items-center gap-1 rounded px-2 py-1 text-xs text-[var(--fg-muted)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] hover:text-red-600 transition"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Excluir
              </button>
            )}

            {/* Toggle status */}
            {task && (
              <button
                type="button"
                disabled={isToggling}
                onClick={() =>
                  isCompleted ? uncompleteMut.mutate() : completeMut.mutate()
                }
                className={cn(
                  'flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition disabled:opacity-50',
                  isCompleted
                    ? 'bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] text-[var(--fg-muted)] hover:bg-[color-mix(in_oklch,var(--fg)_14%,transparent)]'
                    : 'bg-green-600 text-white hover:bg-green-700',
                )}
              >
                {isCompleted ? (
                  <Circle className="h-3.5 w-3.5" />
                ) : (
                  <CheckCircle className="h-3.5 w-3.5" />
                )}
                {isCompleted ? 'Reabrir' : 'Concluir'}
              </button>
            )}

            {/* Fechar */}
            <button
              type="button"
              onClick={onClose}
              aria-label="Fechar"
              className="ml-1 rounded p-1 text-[var(--fg-muted)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] hover:text-[var(--fg)] transition"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Toast inline */}
        {toastMsg && (
          <div className="shrink-0 border-b border-[var(--accent)]/30 bg-[color-mix(in_oklch,var(--accent)_10%,transparent)] px-5 py-1.5 text-center text-xs text-[var(--accent)]">
            {toastMsg}
          </div>
        )}

        {/* Tabs */}
        <div className="flex shrink-0 overflow-x-auto border-b border-[var(--border)] px-5">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'flex shrink-0 items-center gap-1.5 border-b-2 px-1 py-2.5 text-xs font-medium capitalize transition mr-4 whitespace-nowrap',
                activeTab === tab.id
                  ? 'border-[var(--accent)] text-[var(--accent)]'
                  : 'border-transparent text-[var(--fg-muted)] hover:text-[var(--fg)]',
              )}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Corpo */}
        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* Loading / erro */}
          {isLoading && (
            <div className="flex flex-1 items-center justify-center py-16 text-sm text-[var(--fg-muted)]">
              Carregando…
            </div>
          )}
          {error && (
            <div className="flex flex-1 items-center justify-center py-16 text-sm text-red-600">
              Falha ao carregar tarefa.
            </div>
          )}

          {/* Aba Detalhes */}
          {task && activeTab === 'detalhes' && (
            <div className="flex flex-1 min-h-0 overflow-y-auto">
              {/* Coluna principal */}
              <div className="flex-1 min-w-0 overflow-y-auto px-5 py-4 space-y-4">
                {/* Flags */}
                {(task.isImportant ||
                  task.isUrgent ||
                  task.isPrivate ||
                  task.isRecurring ||
                  isOccurrence) && (
                  <div className="flex flex-wrap gap-1.5">
                    {task.isImportant && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700">
                        <AlertTriangle className="h-3 w-3" />
                        Importante
                      </span>
                    )}
                    {task.isUrgent && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-medium text-red-700">
                        <AlertTriangle className="h-3 w-3" />
                        Urgente
                      </span>
                    )}
                    {task.isPrivate && (
                      <span className="rounded-full bg-purple-100 px-2 py-0.5 text-[11px] font-medium text-purple-700">
                        Privada
                      </span>
                    )}
                    {(task.isRecurring || isOccurrence) && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-medium text-blue-700">
                        <RefreshCw className="h-3 w-3" />
                        {isOccurrence ? 'Ocorrência recorrente' : 'Recorrente'}
                      </span>
                    )}
                  </div>
                )}

                {/* Título */}
                <h2 className="text-xl font-semibold leading-snug text-[var(--fg)]">
                  {task.title}
                </h2>

                {/* Descrição — HTML legítimo com linkify; blob cru (DJEN) vira texto puro */}
                {task.description && (
                  looksLikeHtmlDocument(task.description) ? (
                    <div className="text-sm text-[var(--fg-muted)] leading-relaxed whitespace-pre-wrap">
                      {stripHtmlToTextPreserveBreaks(task.description)}
                    </div>
                  ) : (
                    <div
                      className="text-sm text-[var(--fg-muted)] leading-relaxed [&_.mention-chip]:inline-block [&_.mention-chip]:rounded [&_.mention-chip]:bg-yellow-100 [&_.mention-chip]:text-yellow-800 [&_.mention-chip]:px-1 [&_.mention-chip]:font-medium [&_a]:text-[var(--accent)] [&_a]:underline"
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{ __html: linkifyHtml(task.description) }}
                    />
                  )
                )}

                {/* Metadados */}
                <dl className="space-y-2 border-t border-[var(--border)] pt-3">
                  {/* Responsáveis */}
                  <Row label="Responsáveis" icon={<User className="h-3.5 w-3.5" />}>
                    {task.assignees.length === 0 ? (
                      <span className="text-[var(--fg-muted)]">—</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {task.assignees.map((a) => (
                          <span
                            key={a.id}
                            className="relative inline-flex items-center gap-1 rounded-full border border-[var(--border)] bg-[color-mix(in_oklch,var(--fg)_5%,transparent)] px-2 py-0.5 text-xs text-[var(--fg)]"
                          >
                            {/* TODO: ligar hasUnreadMention com dados reais de notificações */}
                            {false && (
                              <span
                                className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-red-500 border border-white"
                                title="Menção não lida"
                              />
                            )}
                            {a.name}
                            {a.role === 'observer' && (
                              <span className="text-[var(--fg-muted)]">(obs.)</span>
                            )}
                          </span>
                        ))}
                      </div>
                    )}
                  </Row>

                  {/* Situação com contador */}
                  <Row label="Situação" icon={<CheckCircle className="h-3.5 w-3.5" />}>
                    {statusInfo && (
                      <span
                        className={cn(
                          'inline-block rounded-full px-2 py-0.5 text-xs font-medium',
                          statusInfo.cls,
                        )}
                      >
                        {statusInfo.label}
                      </span>
                    )}
                  </Row>

                  {/* Data do evento */}
                  <Row label="Data do evento" icon={<Clock className="h-3.5 w-3.5" />}>
                    <span className="text-sm text-[var(--fg)]">
                      {task.dueDate
                        ? task.dueTime && !task.allDay
                          ? formatDate(task.dueDate) + ' às ' + task.dueTime.slice(0, 5)
                          : formatDate(task.dueDate)
                        : '—'}
                      {task.allDay && (
                        <span className="ml-1.5 text-xs text-[var(--fg-muted)]">
                          (dia inteiro)
                        </span>
                      )}
                    </span>
                  </Row>

                  {/* Prazo fatal */}
                  {task.fatalDate && (
                    <Row
                      label="Prazo fatal"
                      icon={<AlertTriangle className="h-3.5 w-3.5 text-red-500" />}
                    >
                      <span className="text-sm font-medium text-red-600">
                        {formatDate(task.fatalDate)}
                      </span>
                    </Row>
                  )}

                  {/* Local */}
                  {task.location && (
                    <Row label="Local" icon={<MapPin className="h-3.5 w-3.5" />}>
                      <span className="text-sm text-[var(--fg)]">{task.location}</span>
                    </Row>
                  )}

                  {/* Criado por */}
                  <Row label="Criado" icon={<User className="h-3.5 w-3.5" />}>
                    <span className="text-sm text-[var(--fg-muted)]">
                      {relativeDate(task.createdAt)}
                    </span>
                  </Row>
                </dl>
              </div>

              {/* Sidebar enriquecida */}
              <aside className="hidden w-64 shrink-0 overflow-y-auto border-l border-[var(--border)] px-4 py-4 space-y-3 md:flex md:flex-col">
                {/* Categoria */}
                {task.category && (
                  <SideCard title="Categoria" icon={<Tag className="h-3.5 w-3.5" />}>
                    <span
                      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium text-white"
                      style={{ backgroundColor: task.category.color }}
                    >
                      {task.category.name}
                    </span>
                  </SideCard>
                )}

                {/* Referência livre (quando não há processo/cliente vinculado) */}
                {task.processReference && !task.caseId && !task.clientId && (
                  <SideCard title="Processo ou caso">
                    <p className="text-xs text-[var(--fg)] leading-relaxed">
                      {task.processReference}
                    </p>
                  </SideCard>
                )}

                {/* Processo + dados vinculados */}
                {caseData && (
                  <SideCard title="Processo">
                    <div className="flex items-start gap-1">
                      <p className="flex-1 text-xs font-mono text-[var(--fg)] break-all leading-relaxed">
                        {caseData.numeroCnj ?? 'Sem número CNJ'}
                      </p>
                      {caseData.numeroCnj && (
                        <button
                          type="button"
                          title="Copiar número CNJ"
                          onClick={() => {
                            copyToClipboard(caseData.numeroCnj!);
                            setToastMsg('Número CNJ copiado');
                          }}
                          className="shrink-0 rounded p-0.5 text-[var(--fg-muted)] hover:text-[var(--fg)] transition"
                        >
                          <Copy className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                    {caseData.actionGroupName && (
                      <p className="mt-1 text-xs text-[var(--fg-muted)]">
                        Grupo: {caseData.actionGroupName}
                      </p>
                    )}
                    {caseData.actionTypeName && (
                      <p className="text-xs text-[var(--fg-muted)]">
                        Tipo: {caseData.actionTypeName}
                      </p>
                    )}
                    {caseData.responsibleUserName && (
                      <p className="text-xs text-[var(--fg-muted)]">
                        Responsável: {caseData.responsibleUserName}
                      </p>
                    )}
                    <a
                      href={`/escritorio/processos/${caseData.id}`}
                      className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-[var(--accent)] hover:underline"
                    >
                      <ExternalLink className="h-3 w-3" />
                      Visualizar processo
                    </a>
                  </SideCard>
                )}

                {/* Cliente com contatos clicaveis */}
                {clientData && (
                  <SideCard title="Partes envolvidas">
                    <p className="text-sm font-medium text-[var(--fg)]">
                      {clientData.name}
                    </p>

                    {/* Telefones / WhatsApp */}
                    {clientPhones.map((c) => {
                      const digits = phoneDigits(c.value);
                      return (
                        <a
                          key={c.id}
                          href={`https://wa.me/55${digits}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-1 flex items-center gap-1 text-xs text-green-600 hover:underline"
                          title="Abrir no WhatsApp"
                        >
                          <Phone className="h-3 w-3" />
                          {c.value}
                        </a>
                      );
                    })}

                    {/* E-mail */}
                    {clientEmailContact && (
                      <a
                        href={`mailto:${clientEmailContact.value}`}
                        className="mt-0.5 flex items-center gap-1 text-xs text-[var(--accent)] hover:underline"
                      >
                        <Mail className="h-3 w-3" />
                        {clientEmailContact.value}
                      </a>
                    )}
                  </SideCard>
                )}

                {/* Vínculo intimação */}
                {task.intimacao && (
                  <SideCard title="Intimação">
                    <p className="text-xs font-mono text-[var(--fg)] break-all">
                      {task.intimacao.numeroProcessoMascara ??
                        task.intimacao.numeroProcesso}
                    </p>
                  </SideCard>
                )}
              </aside>
            </div>
          )}

          {/* Aba Atividade */}
          {task && taskId && activeTab === 'atividade' && (
            <div className="flex-1 overflow-y-auto">
              <ActivityTab
                taskId={taskId}
                tenantId={tenantId}
                clientId={task.clientId}
                caseId={task.caseId}
              />
            </div>
          )}

          {/* Aba E-mail */}
          {task && taskId && activeTab === 'email' && (
            <div className="flex-1 overflow-y-auto">
              <EmailTab
                taskId={taskId}
                taskTitle={task.title}
                defaultEmail={clientEmail}
              />
            </div>
          )}

          {/* Aba Encaminhar */}
          {task && taskId && activeTab === 'encaminhar' && (
            <div className="flex-1 overflow-y-auto">
              <ForwardTab
                taskId={taskId}
                tenantId={tenantId}
                currentAssigneeUserIds={task.assignees.map((a) => a.userId)}
              />
            </div>
          )}

          {/* Aba Historico */}
          {taskId && activeTab === 'historico' && (
            <div className="flex-1 overflow-y-auto">
              <HistoryTab taskId={taskId} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-componentes
// ---------------------------------------------------------------------------

function Row({
  label,
  icon,
  children,
}: {
  label: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-2">
      <dt className="flex w-32 shrink-0 items-center gap-1 pt-0.5 text-xs font-medium text-[var(--fg-muted)]">
        {icon}
        {label}
      </dt>
      <dd className="flex-1">{children}</dd>
    </div>
  );
}

function SideCard({
  title,
  icon,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-md border border-[var(--border)] bg-[color-mix(in_oklch,var(--fg)_3%,transparent)] p-3">
      <p className="mb-1.5 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-[var(--fg-muted)]">
        {icon}
        {title}
      </p>
      {children}
    </div>
  );
}
